/*
 * Report editor vocabulary + starting templates (builder.jsx).
 *
 * AREA_INFO gives each registry dataset a plain name and a one-line
 * description for the "Add a panel" dialog. Anything the registry returns
 * that is not listed here falls back to the registry's own label and its
 * first few number names, so new datasets show up without a code change.
 *
 * Templates (/Reports/new?template=<key>) are built ONLY from what the
 * registry returned for this workspace: a panel whose area, numbers or
 * grouping is missing is skipped (money numbers are absent for managed
 * workspaces, so they simply drop out of the list).
 */

export const AREA_INFO = {
  site_activity: {
    label: "Your website",
    blurb: "Visits, visitors, pages"
  },
  products: {
    label: "Products",
    blurb: "Views and interest per product"
  },
  sales: {
    label: "Sales",
    blurb: "Orders, revenue, customers"
  },
  campaigns: {
    label: "Ads",
    blurb: "Views, clicks, results"
  },
  email: {
    label: "Email",
    blurb: "Sends, opens, clicks"
  },
  social: {
    label: "Social",
    blurb: "Reach, engagement, followers"
  },
  lifecycle: {
    label: "Customers",
    blurb: "Leads, prospects, customers"
  },
  stores: {
    label: "Stores",
    blurb: "Sales at each store"
  },
  markets: {
    label: "Markets",
    blurb: "Website visits in each market"
  },
  geography: {
    label: "Places",
    blurb: "Visits by city, state or country"
  },
  content: {
    label: "Content",
    blurb: "Pieces and how they did"
  },
  territory: {
    label: "Territories",
    blurb: "Sales by rep, region and account"
  },
  territory_reps: {
    label: "Sales reps",
    blurb: "How each rep is doing"
  },
  dealers: {
    label: "Dealers",
    blurb: "How each dealer is doing"
  }
};
export function findArea(registry, key) {
  return (registry && registry.datasets || []).find(d => d.key === key) || null;
}
export function areaLabel(registry, key) {
  if (AREA_INFO[key]) return AREA_INFO[key].label;
  const d = findArea(registry, key);
  return d && d.label || String(key || "").replace(/_/g, " ");
}
export function areaBlurb(registry, key) {
  if (AREA_INFO[key]) return AREA_INFO[key].blurb;
  const d = findArea(registry, key);
  const names = (d && d.metrics || []).slice(0, 3).map(m => String(m.label || m.key).toLowerCase());
  if (!names.length) return "Numbers from this part of your business";
  const s = names.join(", ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// "Date" reads as "Day" in the Group by list.
export function groupLabel(registry, dataset, key) {
  if (key === "date") return "Day";
  const d = findArea(registry, dataset);
  const hit = d && (d.dimensions || []).find(x => x.key === key);
  return hit && hit.label || String(key || "").replace(/_/g, " ");
}

// ------------------------------------------------------------------ templates

// Each panel lists tries in order; the first whose area, grouping and at
// least one number exist wins. Numbers missing from the area are dropped.
const TEMPLATES = {
  monthly: {
    name: "Monthly performance",
    comparison: "previous_period",
    panels: [{
      kind: "kpi",
      title: "Headline numbers",
      width: "full",
      tries: [{
        dataset: "site_activity",
        metrics: ["impressions", "visitors", "sessions", "conversions", "conversion_value"]
      }]
    }, {
      kind: "line",
      title: "Visits by day",
      width: "full",
      tries: [{
        dataset: "site_activity",
        metrics: ["sessions"],
        dim: "date"
      }]
    }, {
      kind: "table",
      title: "Sales by store",
      width: "half",
      tries: [{
        dataset: "stores",
        metrics: ["revenue", "orders"],
        dim: "store",
        top: 10
      }]
    }]
  },
  products: {
    name: "Products people look at",
    panels: [{
      kind: "table",
      title: "Most viewed products",
      width: "full",
      tries: [{
        dataset: "products",
        metrics: ["views", "visitors", "conversions"],
        dim: "product",
        top: 10
      }]
    }, {
      kind: "line",
      title: "Product views by day",
      width: "half",
      tries: [{
        dataset: "products",
        metrics: ["views"],
        dim: "date"
      }]
    }, {
      kind: "bar",
      title: "Views by category",
      width: "half",
      tries: [{
        dataset: "products",
        metrics: ["views"],
        dim: "category",
        top: 10
      }]
    }]
  },
  marketing: {
    name: "Marketing overview",
    panels: [{
      kind: "kpi",
      title: "Ads",
      width: "full",
      tries: [{
        dataset: "campaigns",
        metrics: ["impressions", "clicks", "ctr", "video_views", "spend"]
      }]
    }, {
      kind: "kpi",
      title: "Email",
      width: "full",
      tries: [{
        dataset: "email",
        metrics: ["sent", "delivered", "opens", "clicks", "revenue"]
      }]
    }, {
      kind: "kpi",
      title: "Social",
      width: "full",
      tries: [{
        dataset: "social",
        metrics: ["reach", "impressions", "engagements", "followers"]
      }]
    }]
  },
  sales: {
    name: "Sales by customer",
    panels: [{
      kind: "kpi",
      title: "Sales numbers",
      width: "full",
      tries: [{
        dataset: "sales",
        metrics: ["revenue", "orders", "customers", "aov"]
      }, {
        dataset: "stores",
        metrics: ["revenue", "orders", "units"]
      }]
    }, {
      kind: "table",
      title: "Top customers",
      width: "full",
      tries: [{
        dataset: "sales",
        metrics: ["revenue", "orders", "units"],
        dim: "dealer",
        top: 10
      }, {
        dataset: "dealers",
        metrics: ["revenue", "orders", "units"],
        dim: "dealer",
        top: 10
      }, {
        dataset: "sales",
        metrics: ["revenue", "orders", "customers"],
        dim: "customer_type",
        top: 10
      }]
    }, {
      kind: "bar",
      title: "Revenue by region",
      width: "full",
      tries: [{
        dataset: "sales",
        metrics: ["revenue"],
        dim: "region",
        top: 10
      }, {
        dataset: "territory",
        metrics: ["revenue"],
        dim: "region",
        top: 10
      }, {
        dataset: "dealers",
        metrics: ["revenue"],
        dim: "region",
        top: 10
      }]
    }]
  },
  blank: {
    name: "",
    panels: []
  }
};
export const TEMPLATE_KEYS = Object.keys(TEMPLATES);
function resolveTry(registry, t) {
  const d = findArea(registry, t.dataset);
  if (!d) return null;
  const have = new Set((d.metrics || []).map(m => m.key));
  const metrics = t.metrics.filter(m => have.has(m));
  if (!metrics.length) return null;
  if (t.dim && !(d.dimensions || []).some(x => x.key === t.dim)) return null;
  return {
    dataset: d.key,
    metrics,
    dimensions: t.dim ? [t.dim] : [],
    top: t.top || null,
    sortBy: t.top ? metrics[0] : null
  };
}

/** {name, comparison?, panels} for a template key, or null when the key is unknown. */
export function buildTemplate(registry, key) {
  const tpl = TEMPLATES[String(key || "").toLowerCase()];
  if (!tpl) return null;
  const panels = [];
  tpl.panels.forEach(spec => {
    let hit = null;
    for (const t of spec.tries) {
      hit = resolveTry(registry, t);
      if (hit) break;
    }
    if (!hit) return;
    panels.push({
      kind: spec.kind,
      title: spec.title,
      ...hit,
      filters: {},
      sortDir: "desc",
      series: null,
      width: spec.width || "full"
    });
  });
  return {
    name: tpl.name,
    comparison: tpl.comparison || null,
    panels
  };
}